import { track } from '@/lib/analytics';

/**
 * Per-match counters of the sync engine, sent once to PostHog as
 * `live_match_summary` when the server confirms the match end (VOL-208). They
 * give each match a baseline: fewer corrections and a shorter confirmation
 * delay from one release to the next.
 *
 * Persisted: a match entered over several page loads (reload, app killed by the
 * OS) must still add up to one summary.
 */
export const LIVE_MATCH_SUMMARY_STORAGE_KEY = 'vs.live-match-summary';

/** Enough for the matches a tablet may leave unclosed. */
const MAX_TRACKED_MATCHES = 10;

interface LiveMatchCounters {
  /** Actions queued for the server, structural rows (set start, match end) excluded. */
  actions: number;
  /** Actions removed by the coach (« Annuler », history). */
  undos: number;
  /** Actions edited after entry (point-end screen, history). */
  corrections: number;
  /** Longest queue of this match seen on the tablet. */
  maxQueue: number;
  /** Failed sends of this match's actions. */
  resends: number;
  /** Queue-to-final-ack delays, in ms. */
  ackDelays: number[];
  /** Last change, to forget the oldest matches first. */
  updatedAt: number;
}

type CountersByMatch = Record<string, LiveMatchCounters>;

function emptyCounters(): LiveMatchCounters {
  return { actions: 0, undos: 0, corrections: 0, maxQueue: 0, resends: 0, ackDelays: [], updatedAt: Date.now() };
}

function isCounters(value: unknown): value is LiveMatchCounters {
  if (!value || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return ['actions', 'undos', 'corrections', 'maxQueue', 'resends', 'updatedAt'].every((k) => typeof c[k] === 'number')
    && Array.isArray(c.ackDelays);
}

function read(): CountersByMatch {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(LIVE_MATCH_SUMMARY_STORAGE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(Object.entries(parsed).filter(([, v]) => isCounters(v))) as CountersByMatch;
  } catch {
    return {};
  }
}

function write(all: CountersByMatch): void {
  const kept = Object.entries(all)
    .sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_TRACKED_MATCHES);
  try {
    localStorage.setItem(LIVE_MATCH_SUMMARY_STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // Storage blocked or full: the summary is only a measure, the capture goes on.
  }
}

function update(matchId: string | null, change: (c: LiveMatchCounters) => void): void {
  if (!matchId) return;
  const all = read();
  const counters = all[matchId] ?? emptyCounters();
  change(counters);
  counters.updatedAt = Date.now();
  all[matchId] = counters;
  write(all);
}

export function recordQueuedAction(matchId: string | null, queueSize: number): void {
  update(matchId, (c) => {
    c.actions += 1;
    c.maxQueue = Math.max(c.maxQueue, queueSize);
  });
}

export function recordUndo(matchId: string | null): void {
  update(matchId, (c) => {
    c.undos += 1;
  });
}

export function recordCorrection(matchId: string | null): void {
  update(matchId, (c) => {
    c.corrections += 1;
  });
}

export function recordResends(matchId: string, count: number): void {
  if (count <= 0) return;
  update(matchId, (c) => {
    c.resends += count;
  });
}

export function recordAckDelays(matchId: string, delaysMs: number[]): void {
  const valid = delaysMs.filter((d) => Number.isFinite(d) && d >= 0);
  if (valid.length === 0) return;
  update(matchId, (c) => {
    c.ackDelays.push(...valid.map(Math.round));
  });
}

/** Nearest-rank percentile; 0 for no value. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1)];
}

/** Sends the match's summary once, then forgets its counters. */
export function sendLiveMatchSummary(matchId: string): void {
  const all = read();
  const counters = all[matchId];
  if (!counters) return;
  delete all[matchId];
  write(all);
  track('live_match_summary', {
    match_id: matchId,
    actions: counters.actions,
    undos: counters.undos,
    corrections: counters.corrections,
    max_queue: counters.maxQueue,
    resends: counters.resends,
    ack_p95_ms: percentile(counters.ackDelays, 95),
    ack_median_ms: percentile(counters.ackDelays, 50),
  });
}
